import React from 'react';
import styled, { keyframes } from 'styled-components';

/* ── Theme ─────────────────────────────────────────────────── */
const GOLD       = '#C9952A';
const GOLD_LIGHT = '#E8B84B';
const CREAM      = '#FAF6ED';
const WARM_WHITE = '#FFFCF5';
const DARK       = '#1C1208';
const TEXT       = '#2A1F0E';
const TEXT_MID   = '#6B5540';

const float = keyframes`
  0%   { transform: translateY(0px); }
  50%  { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

const shimmer = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

/* ── Icon helper ────────────────────────────────────────────── */
const IconWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(201,149,42,0.1), rgba(232,184,75,0.06));
  border: 1.5px solid rgba(201,149,42,0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.2rem;
`;

const GoldIcon = ({ children }) => (
  <IconWrap>
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none"
         stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  </IconWrap>
);

const PageContainer = styled.div`
  background: ${CREAM};
  overflow: hidden;
`;

const Hero = styled.section`
  position: relative;
  min-height: 72vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 2rem 8rem;
  background: linear-gradient(135deg, #1A0A1C 0%, #28102A 55%, #1A0A1C 100%);
  overflow: hidden;
  clip-path: polygon(0 0, 100% 0, 100% 88%, 0 100%);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse at 50% -10%, rgba(201,149,42,0.28) 0%, transparent 55%),
      radial-gradient(ellipse at 5%  90%, rgba(201,149,42,0.10) 0%, transparent 40%),
      radial-gradient(ellipse at 95% 90%, rgba(201,149,42,0.10) 0%, transparent 40%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='15' y='15' width='30' height='30' fill='none' stroke='rgba(201%2C149%2C42%2C0.05)' stroke-width='1'/%3E%3Ccircle cx='30' cy='30' r='4' fill='rgba(201%2C149%2C42%2C0.04)'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 0;
  }
`;

const HeroContent = styled.div`
  max-width: 860px;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const Sanskrit = styled.h2`
  font-size: 2.2rem;
  color: rgba(232,184,75,0.65);
  margin-bottom: 0.6rem;
  font-family: 'Playfair Display', serif;
  font-weight: 400;
  font-style: italic;
`;

const HeroTitle = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: clamp(3rem, 6vw, 4.8rem);
  font-weight: 900;
  margin-bottom: 0.6rem;
  line-height: 1.05;
  background: linear-gradient(135deg, #fff 0%, ${GOLD_LIGHT} 50%, #fff 100%);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmer} 6s ease infinite, ${float} 7s ease-in-out infinite;
`;

const HeroSub = styled.h3`
  font-size: 1.15rem;
  color: rgba(255,255,255,0.65);
  font-weight: 400;
  font-family: 'Source Serif 4', serif;
  margin-top: 0.8rem;
`;

const GoldRule = styled.div`
  width: 60px;
  height: 1.5px;
  background: linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT});
  margin: 1.4rem auto;
  border-radius: 2px;
`;

const MainContent = styled.div`
  max-width: 1100px;
  margin: -4rem auto 0;
  padding: 0 2rem 5rem;
  position: relative;
  z-index: 2;
`;

const Section = styled.section`
  background: ${WARM_WHITE};
  border-radius: 20px;
  padding: 2.8rem;
  margin-bottom: 2rem;
  box-shadow: 0 8px 28px rgba(201,149,42,0.09);
  border: 1px solid rgba(201,149,42,0.12);
`;

const SectionTitle = styled.h2`
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.6rem, 2.5vw, 2.1rem);
  color: ${TEXT};
  margin-bottom: 1.8rem;
  text-align: center;
  position: relative;

  &::after {
    content: '';
    display: block;
    width: 52px;
    height: 2px;
    background: linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT});
    border-radius: 2px;
    margin: 0.75rem auto 0;
  }
`;

const Text = styled.p`
  font-size: 1rem;
  line-height: 1.85;
  color: ${TEXT_MID};
  margin-bottom: 1.2rem;
  font-family: 'Source Serif 4', serif;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const FeatureCard = styled.div`
  background: ${CREAM};
  border-radius: 18px;
  padding: 2.2rem;
  border-top: 3px solid ${GOLD};
  text-align: center;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 14px 38px rgba(201,149,42,0.14);
  }

  h3 {
    font-family: 'Playfair Display', serif;
    font-size: 1.25rem;
    color: ${TEXT};
    margin-bottom: 0.65rem;
  }
`;

const CTASection = styled(Section)`
  text-align: center;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT});
  color: ${DARK};
  text-decoration: none;
  padding: 0.9rem 2.2rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Source Serif 4', serif;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(201,149,42,0.35);
  margin-top: 1.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(201,149,42,0.5);
  }
`;

const Ranjana = () => (
  <PageContainer>
    <Hero>
      <HeroContent>
        <Sanskrit>रञ्जन</Sanskrit>
        <HeroTitle>Rañjanā</HeroTitle>
        <GoldRule />
        <HeroSub>Heritage Gaming &amp; Entertainment</HeroSub>
      </HeroContent>
    </Hero>

    <MainContent>
      <Section>
        <SectionTitle>Interactive Experience</SectionTitle>
        <Text style={{ textAlign: 'center', maxWidth: '640px', margin: '0 auto 2rem' }}>
          Ranjana transforms traditional cultural content into engaging interactive experiences
          through gamification and digital storytelling.
        </Text>
        <FeatureGrid>
          <FeatureCard>
            <GoldIcon>
              {/* Game controller */}
              <line x1="6" y1="12" x2="10" y2="12"/>
              <line x1="8" y1="10" x2="8" y2="14"/>
              <line x1="15" y1="13" x2="15.01" y2="13" strokeWidth="3"/>
              <line x1="18" y1="11" x2="18.01" y2="11" strokeWidth="3"/>
              <rect x="2" y="6" width="20" height="12" rx="3"/>
            </GoldIcon>
            <h3>Cultural Games</h3>
            <Text style={{ marginBottom: 0 }}>Immersive games based on Indian mythology, history, and traditions.</Text>
          </FeatureCard>
          <FeatureCard>
            <GoldIcon>
              {/* Open book — stories */}
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
            </GoldIcon>
            <h3>Digital Stories</h3>
            <Text style={{ marginBottom: 0 }}>Interactive storytelling bringing ancient tales to modern audiences.</Text>
          </FeatureCard>
          <FeatureCard>
            <GoldIcon>
              {/* Star — education */}
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </GoldIcon>
            <h3>Educational Fun</h3>
            <Text style={{ marginBottom: 0 }}>Learn while playing with carefully crafted educational content.</Text>
          </FeatureCard>
        </FeatureGrid>
      </Section>

      <CTASection>
        <SectionTitle>Try Our App</SectionTitle>
        <Text style={{ maxWidth: '560px', margin: '0 auto' }}>
          Experience the magic of Indian heritage through interactive games and stories!
        </Text>
        <CTAButton href="https://calm-rabanadas-b320df.netlify.app/" target="_blank" rel="noopener noreferrer">
          🎮 Explore Our App
        </CTAButton>
      </CTASection>
    </MainContent>
  </PageContainer>
);

export default Ranjana;
